import { Stack, styled, SxProps, alpha } from '@mui/material';

import palette from '../theme/palette';

const Container = styled('div')<{
  minWidth: string;
}>(({ minWidth }) => ({
  display: 'grid',
  gap: 19,
  gridTemplateColumns: `repeat(auto-fit, minmax(${minWidth}, 1fr))`,
  width: '100%',
  boxSizing: 'border-box'
}));

const Option = styled('button')<{ selected: boolean }>(({ selected }) => ({
  display: 'flex',
  flexGrow: 1,
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: 8,
  padding: '16px',
  borderRadius: 8,
  border: '1px solid',
  borderColor: selected ? palette.black : palette.grey[1200],
  background: selected ? palette.grey[100] : 'transparent',
  cursor: 'pointer',
  outline: 'none',
  transition: 'background 0.2s, box-shadow 0.2s',
  color: selected ? palette.black : palette.grey[400],
  '&:hover': {
    background: alpha(palette.black, 0.02)
  }
}));

const Radio = styled('span')({
  width: 20,
  height: 20,
  borderRadius: '50%',
  border: `2px solid ${palette.grey[1200]}`,
  background: 'transparent',
  display: 'flex',
  alignSelf: 'baseline',
  justifyContent: 'center',
  marginLeft: 8
});

const Dot = styled('span')({
  width: 12,
  height: 12,
  borderRadius: '50%',
  background: palette.black,
  margin: 'auto'
});

export interface MultiToggleOption {
  label: string;
  icon?: React.ReactNode;
  value: string;
}

interface MultiToggleProps {
  options: MultiToggleOption[];
  value: string;
  onChange: (value: string) => void;
  sx?: SxProps;
  minWidth?: string;
}

const MultiToggle = ({ options, value, onChange, sx, minWidth = '100px' }: MultiToggleProps) => (
  <Container sx={sx} minWidth={minWidth}>
    {options.map(opt => (
      <Option
        key={opt.value}
        selected={opt.value === value}
        type="button"
        onClick={() => onChange(opt.value)}
        style={{ minWidth }}
      >
        <Stack direction="row" alignItems="center">
          {opt.icon}
          <span>{opt.label}</span>
        </Stack>
        <Radio>{opt.value === value && <Dot />}</Radio>
      </Option>
    ))}
  </Container>
);

export default MultiToggle;
